import React, { useState } from "react";
import { FaTimes, FaEye, FaEyeSlash, FaKey, FaUser, FaEnvelope, FaMagic } from "react-icons/fa";
import { generateStrongPassword } from "../../utils/passwordUtils.ts";
import { useTheme } from "../../contexts/ThemeContext.tsx";
import { getTheme } from "../../theme.ts";

interface PasswordResetModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (newPassword: string, confirmPassword: string) => Promise<void>;
    userName: string;
    userEmail: string;
    userRole: string;
}

const PasswordResetModal: React.FC<PasswordResetModalProps> = ({
    isOpen,
    onClose,
    onSubmit,
    userName,
    userEmail,
    userRole,
}) => {
    const { themeName } = useTheme();
    const currentTheme = getTheme(themeName);

    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    // Password strength calculation
    const getPasswordStrength = (password: string): { strength: number; label: string; color: string } => {
        if (!password) return { strength: 0, label: "", color: "" };

        let strength = 0;
        if (password.length >= 8) strength++;
        if (password.length >= 12) strength++;
        if (/[a-z]/.test(password)) strength++;
        if (/[A-Z]/.test(password)) strength++;
        if (/[0-9]/.test(password)) strength++;
        if (/[^a-zA-Z0-9]/.test(password)) strength++;

        if (strength <= 2) return { strength: 1, label: "Weak", color: "#ef4444" };
        if (strength <= 4) return { strength: 2, label: "Medium", color: "#f59e0b" };
        return { strength: 3, label: "Strong", color: "#10b981" };
    };

    const passwordStrength = getPasswordStrength(newPassword);

    const validatePassword = (): boolean => {
        setError("");

        if (!newPassword || !confirmPassword) {
            setError("Please fill in all fields");
            return false;
        }

        if (newPassword.length < 8) {
            setError("Password must be at least 8 characters long");
            return false;
        }

        if (!/[A-Z]/.test(newPassword)) {
            setError("Password must contain at least one uppercase letter");
            return false;
        }

        if (!/[a-z]/.test(newPassword)) {
            setError("Password must contain at least one lowercase letter");
            return false;
        }

        if (!/[0-9]/.test(newPassword)) {
            setError("Password must contain at least one number");
            return false;
        }

        if (!/[^a-zA-Z0-9]/.test(newPassword)) {
            setError("Password must contain at least one special character");
            return false;
        }

        if (newPassword !== confirmPassword) {
            setError("Passwords do not match");
            return false;
        }

        return true;
    };

    const handleSubmit = async () => {
        if (!validatePassword()) return;

        setIsSubmitting(true);
        setError("");
        setSuccess("");

        try {
            await onSubmit(newPassword, confirmPassword);
            setSuccess("Password reset successfully!");
            setTimeout(() => {
                handleClose();
            }, 2000);
        } catch (err: any) {
            console.error("Password reset failed:", err);
            setError(err.response?.data?.message || err.message || "Failed to reset password. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        setNewPassword("");
        setConfirmPassword("");
        setShowNewPassword(false);
        setShowConfirmPassword(false);
        setError("");
        setSuccess("");
        onClose();
    };

    const handleGeneratePassword = () => {
        const password = generateStrongPassword();
        setNewPassword(password);
        setConfirmPassword(password);
        setShowNewPassword(true);
        setShowConfirmPassword(true);
        setError("");
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 flex items-center justify-center z-[102]" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="rounded-lg shadow-2xl w-[500px] max-h-[90vh] overflow-y-auto" style={{ backgroundColor: currentTheme.surface }}>
                {/* Header */}
                <div className="px-6 py-4 border-b flex items-center justify-between" style={{ backgroundColor: currentTheme.primary, borderColor: currentTheme.border }}>
                    <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full flex items-center justify-center" style={{ backgroundColor: currentTheme.surface }}>
                            <FaKey size={20} style={{ color: currentTheme.primary }} />
                        </div>
                        <h3 className="text-xl font-semibold" style={{ color: currentTheme.inverseText }}>Reset Password</h3>
                    </div>
                    <button
                        onClick={handleClose}
                        className="transition-colors hover:bg-white hover:bg-opacity-20 p-1 rounded"
                        style={{ color: currentTheme.inverseText }}
                        disabled={isSubmitting}
                    >
                        <FaTimes size={20} />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 space-y-6">
                    {/* User Info */}
                    <div className="rounded-lg p-4 space-y-3" style={{ backgroundColor: currentTheme.card }}>
                        <div className="flex items-center gap-3">
                            <FaUser style={{ color: currentTheme.primary }} />
                            <div>
                                <p className="text-sm" style={{ color: currentTheme.textSecondary }}>User</p>
                                <p className="font-medium" style={{ color: currentTheme.text }}>{userName}</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <FaEnvelope style={{ color: currentTheme.primary }} />
                            <div>
                                <p className="text-sm" style={{ color: currentTheme.textSecondary }}>Email</p>
                                <p className="font-medium" style={{ color: currentTheme.text }}>{userEmail}</p>
                            </div>
                        </div>
                        <div className="inline-block px-3 py-1 rounded-full text-sm" style={{ backgroundColor: currentTheme.primary, color: currentTheme.inverseText }}>
                            {userRole}
                        </div>
                    </div>

                    {/* Success Message */}
                    {success && (
                        <div className="px-4 py-3 rounded-lg border" style={{ backgroundColor: '#10b98120', borderColor: '#10b981', color: '#10b981' }}>
                            {success}
                        </div>
                    )}

                    {/* Error Message */}
                    {error && (
                        <div className="px-4 py-3 rounded-lg border" style={{ backgroundColor: `${currentTheme.error}20`, borderColor: currentTheme.error, color: currentTheme.error }}>
                            {error}
                        </div>
                    )}

                    {/* New Password */}
                    <div>
                        <div className="flex justify-between items-center mb-2">
                            <label className="block text-sm font-medium" style={{ color: currentTheme.text }}>
                                New Password
                            </label>
                            <button
                                type="button"
                                onClick={handleGeneratePassword}
                                className="text-xs flex items-center gap-1 hover:underline"
                                style={{ color: currentTheme.primary }}
                                disabled={isSubmitting}
                            >
                                <FaMagic /> Generate Strong Password
                            </button>
                        </div>
                        <div className="relative">
                            <input
                                type={showNewPassword ? "text" : "password"}
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                className="w-full px-4 py-2 pr-10 rounded-lg border focus:outline-none focus:ring-2"
                                style={{
                                    backgroundColor: currentTheme.surface,
                                    color: currentTheme.text,
                                    borderColor: currentTheme.border
                                }}
                                placeholder="Enter new password"
                                disabled={isSubmitting}
                            />
                            <button
                                type="button"
                                onClick={() => setShowNewPassword(!showNewPassword)}
                                className="absolute right-3 top-1/2 transform -translate-y-1/2"
                                style={{ color: currentTheme.textSecondary }}
                                disabled={isSubmitting}
                            >
                                {showNewPassword ? <FaEyeSlash size={18} /> : <FaEye size={18} />}
                            </button>
                        </div>

                        {/* Password Strength Indicator */}
                        {newPassword && (
                            <div className="mt-2">
                                <div className="flex items-center justify-between mb-1">
                                    <span className="text-xs" style={{ color: currentTheme.textSecondary }}>Password Strength:</span>
                                    <span className="text-xs font-medium" style={{ color: passwordStrength.color }}>{passwordStrength.label}</span>
                                </div>
                                <div className="h-2 rounded-full overflow-hidden" style={{ backgroundColor: currentTheme.card }}>
                                    <div
                                        className="h-full transition-all duration-300"
                                        style={{
                                            width: `${(passwordStrength.strength / 3) * 100}%`,
                                            backgroundColor: passwordStrength.color
                                        }}
                                    />
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Confirm Password */}
                    <div>
                        <label className="block text-sm font-medium mb-2" style={{ color: currentTheme.text }}>
                            Confirm Password
                        </label>
                        <div className="relative">
                            <input
                                type={showConfirmPassword ? "text" : "password"}
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                className="w-full px-4 py-2 pr-10 rounded-lg border focus:outline-none focus:ring-2"
                                style={{
                                    backgroundColor: currentTheme.surface,
                                    color: currentTheme.text,
                                    borderColor: currentTheme.border
                                }}
                                placeholder="Confirm new password"
                                disabled={isSubmitting}
                            />
                            <button
                                type="button"
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                className="absolute right-3 top-1/2 transform -translate-y-1/2"
                                style={{ color: currentTheme.textSecondary }}
                                disabled={isSubmitting}
                            >
                                {showConfirmPassword ? <FaEyeSlash size={18} /> : <FaEye size={18} />}
                            </button>
                        </div>
                    </div>

                    {/* Password Requirements */}
                    <div className="rounded-lg p-4 text-xs space-y-1" style={{ backgroundColor: currentTheme.card, color: currentTheme.textSecondary }}>
                        <p className="font-medium mb-2">Password must contain:</p>
                        <p>• At least 8 characters</p>
                        <p>• At least one uppercase letter</p>
                        <p>• At least one lowercase letter</p>
                        <p>• At least one number</p>
                        <p>• At least one special character</p>
                    </div>
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t flex justify-end gap-3" style={{ backgroundColor: currentTheme.card, borderColor: currentTheme.border }}>
                    <button
                        onClick={handleClose}
                        className="px-6 py-2 rounded-lg font-medium transition-colors border"
                        style={{
                            backgroundColor: currentTheme.surface,
                            color: currentTheme.text,
                            borderColor: currentTheme.border
                        }}
                        disabled={isSubmitting}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        className="px-6 py-2 rounded-lg font-medium transition-colors"
                        style={{
                            backgroundColor: currentTheme.primary,
                            color: currentTheme.inverseText,
                            opacity: isSubmitting ? 0.6 : 1
                        }}
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? "Resetting..." : "Reset Password"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PasswordResetModal;
